import type { EnrichedOpportunity } from '@/models/marketData';
import { getSignalLabel } from '@/utils/marketCalculations';

export const exportOpportunitiesToExcel = async (
  opportunities: EnrichedOpportunity[],
): Promise<void> => {
  if (opportunities.length === 0) throw new Error('No opportunities match the current filters.');
  const XLSX = await import('xlsx');
  const rows = opportunities.map((item) => ({
    Symbol: item.tradeInstrument.symbol,
    'Company Name': item.tradeInstrument.name,
    Exchange: item.tradeInstrument.exchange,
    'Instrument Key': item.tradeInstrument.instrumentKey,
    Signal: getSignalLabel(item.signal),
    Score: item.score,
    'Confidence (%)': item.confidence,
    'Close (INR)': item.indicators.close,
    'Watch Zone (INR)': item.suggestedEntry,
    'Risk Reference (INR)': item.stopLoss,
    'Scenario 1 (INR)': item.target1,
    'Scenario 2 (INR)': item.target2,
    'Risk (%)': Number(item.metrics.riskPercent.toFixed(2)),
    'Scenario 1 Upside (%)': Number(item.metrics.target1UpsidePercent.toFixed(2)),
    'Scenario 2 Upside (%)': Number(item.metrics.target2UpsidePercent.toFixed(2)),
    'Scenario 1 R:R': Number(item.metrics.target1RiskReward.toFixed(2)),
    'Scenario 2 R:R': Number(item.metrics.target2RiskReward.toFixed(2)),
    'EMA 20 (INR)': item.indicators.ema20,
    'EMA 50 (INR)': item.indicators.ema50,
    'EMA 200 (INR)': item.indicators.ema200,
    'EMA Alignment': item.metrics.emaAligned ? 'Full alignment' : 'Mixed',
    'RSI 14': item.indicators.rsi14,
    'ADX 14': item.indicators.adx14,
    'ATR 14': item.indicators.atr14,
    'Volume Ratio (20D)': item.indicators.volumeRatio20,
    MACD: item.indicators.macd,
    'MACD Signal': item.indicators.macdSignal,
    'MACD State': item.metrics.macdBullish ? 'Bullish' : 'Below signal',
    'Fresh MACD Crossover': item.metrics.freshMacdCrossover ? 'Yes' : 'No',
    'SuperTrend (INR)': item.indicators.superTrend,
    'SuperTrend Bullish': item.indicators.superTrendBullish ? 'Yes' : 'No',
    'News Sentiment': item.metrics.newsSentiment.label,
    'News Score': item.metrics.newsSentiment.score,
    'Related News Count': item.relatedNews.length,
    'Decision Rationale': item.reasons.join(' | '),
    'Evaluated At (UTC)': item.evaluatedAtUtc,
  }));

  const worksheet = XLSX.utils.json_to_sheet(rows);
  worksheet['!cols'] = Object.keys(rows[0]).map((heading) => ({
    wch: Math.min(48, Math.max(12, heading.length + 2)),
  }));
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Opportunity Radar');
  XLSX.writeFile(workbook, `TradeXRadar_${new Date().toISOString().slice(0, 10)}.xlsx`, {
    compression: true,
  });
};
